import React, { useMemo, useCallback } from 'react';
import { Modal } from 'antd';
import type { ModalProps as AntModalProps } from 'antd';
import { useAndStore } from '../../base/store/useAndStore';
import { getDataSetStoreKey } from '../../base/storeKeyUtils';
import type { BaseComponentProps, DataSetConfig } from '../../types/common';

/**
 * AndModal Props 接口
 */
export interface AndModalProps extends Omit<AntModalProps, 'onOk' | 'onCancel'>, BaseComponentProps, DataSetConfig {
  /** 确认动作 */
  okAction?: string;
  /** 取消动作 */
  cancelAction?: string;
  /** 确认回调 */
  onOk?: () => void;
  /** 取消回调 */
  onCancel?: () => void;
}

/**
 * AndModal 组件
 * 封装 Ant Design Modal，支持数据绑定和动作解析
 */
export const AndModal: React.FC<AndModalProps> = ({
  id,
  sessionId,
  develop,
  dataSet,
  dataSetField,
  okAction,
  cancelAction,
  onOk,
  onCancel,
  open: propOpen,
  children,
  ...restProps
}) => {
  const addCommand = useAndStore((state) => state.addCommand);
  const getDataSet = useAndStore((state) => state.getDataSet);
  const setDataSet = useAndStore((state) => state.setDataSet);
  const dataSetKey = useMemo(
    () => getDataSetStoreKey(sessionId, dataSet),
    [sessionId, dataSet]
  );

  // 从数据集获取 open 状态
  const storeOpen = useMemo(() => {
    if (dataSetKey && dataSetField) {
      return getDataSet(dataSetKey, dataSetField);
    }
    return undefined;
  }, [dataSetKey, dataSetField, getDataSet]);

  const displayOpen = storeOpen !== undefined ? Boolean(storeOpen) : propOpen;

  // 确认处理
  const handleOk = useCallback(() => {
    if (onOk) {
      onOk();
    }

    // 关闭 Modal
    if (dataSetKey && dataSetField) {
      setDataSet(dataSetKey, dataSetField, false);
    }

    // 处理确认动作
    if (okAction && sessionId) {
      addCommand(sessionId, {
        actionName: okAction,
        arg: null,
        timestamp: Date.now(),
      });
    }
  }, [onOk, dataSetKey, dataSetField, setDataSet, okAction, sessionId, addCommand]);

  // 取消处理
  const handleCancel = useCallback(() => {
    if (onCancel) {
      onCancel();
    }

    // 关闭 Modal
    if (dataSetKey && dataSetField) {
      setDataSet(dataSetKey, dataSetField, false);
    }

    // 处理取消动作
    if (cancelAction && sessionId) {
      addCommand(sessionId, {
        actionName: cancelAction,
        arg: null,
        timestamp: Date.now(),
      });
    }
  }, [onCancel, dataSetKey, dataSetField, setDataSet, cancelAction, sessionId, addCommand]);

  return (
    <Modal
      {...restProps}
      open={displayOpen}
      onOk={handleOk}
      onCancel={handleCancel}
    >
      {children}
    </Modal>
  );
};

export default AndModal;
